import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, ArrowLeft, BookOpen, Flame, Star, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const PublicAchievements = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicProfile();
  }, [userId]);

  const fetchPublicProfile = async () => {
    try {
      const response = await axios.get(`${API}/achievements/${userId}`);
      setUserData(response.data.user);
      setBadges(response.data.badges);
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
      setError('User not found or profile is private');
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'from-gray-500 to-gray-600';
      case 'rare': return 'from-blue-500 to-blue-600';
      case 'epic': return 'from-purple-500 to-purple-600';
      case 'legendary': return 'from-yellow-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getRarityBorder = (rarity) => {
    switch (rarity) {
      case 'common': return 'border-gray-500/30';
      case 'rare': return 'border-blue-500/30';
      case 'epic': return 'border-purple-500/30';
      case 'legendary': return 'border-yellow-500/30';
      default: return 'border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-red-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Award className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Profile Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back
        </button>

        {/* User Header */}
        <div className="bg-gradient-to-r from-red-900/30 to-purple-900/30 rounded-2xl p-8 border border-white/10 mb-8">
          <div className="flex items-center space-x-6">
            {/* Avatar */}
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-purple-600 rounded-full flex items-center justify-center text-3xl font-bold text-white">
              {userData?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white">{userData?.name || 'Reader'}</h1>
              <p className="text-gray-400 mt-1">Seven Chapter Books Member</p>
              
              {/* Stats Row */}
              <div className="flex items-center space-x-6 mt-4">
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-yellow-500" />
                  <span className="text-white font-semibold">{badges.length}</span>
                  <span className="text-gray-400">Badges</span>
                </div>
                {userData?.stats && (
                  <>
                    <div className="flex items-center space-x-2">
                      <BookOpen className="h-5 w-5 text-blue-500" />
                      <span className="text-white font-semibold">{userData.stats.books_completed || 0}</span>
                      <span className="text-gray-400">Books</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Flame className="h-5 w-5 text-orange-500" />
                      <span className="text-white font-semibold">{userData.stats.current_streak || 0}</span>
                      <span className="text-gray-400">Day Streak</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-6">
            <Award className="h-6 w-6 text-yellow-500" />
            <h2 className="text-2xl font-bold text-white">Earned Badges</h2>
          </div>

          {badges.length === 0 ? (
            <div className="bg-white/5 rounded-xl p-8 text-center border border-white/10">
              <Award className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No badges earned yet. Start reading to earn achievements!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {badges.map((badge) => (
                <div
                  key={badge.id}
                  className={`relative p-4 rounded-xl border bg-gradient-to-br ${getRarityColor(badge.rarity)} bg-opacity-20 ${getRarityBorder(badge.rarity)} hover:scale-105 transition-transform`}
                  data-testid={`public-badge-${badge.id}`}
                >
                  {/* Badge Icon */}
                  <div className="text-center">
                    <span className="text-4xl">{badge.icon}</span>
                  </div>
                  
                  {/* Badge Info */}
                  <div className="mt-3 text-center">
                    <h4 className="text-sm font-semibold text-white">{badge.name}</h4>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{badge.description}</p>
                    
                    {/* Rarity Tag */}
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full capitalize bg-gradient-to-r ${getRarityColor(badge.rarity)} text-white`}>
                      {badge.rarity}
                    </span>
                    
                    {/* Earned Date */}
                    {badge.earned_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(badge.earned_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm mb-4">
            Join Seven Chapter Books and start earning badges!
          </p>
          <Button
            onClick={() => navigate('/contact')}
            className="bg-red-600 hover:bg-red-700"
          >
            Request Access
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublicAchievements;
